import { createContext, useState } from 'react';
import { useModal } from 'src/services/modal';

const LeasedAssetTableContext = createContext();

export default LeasedAssetTableContext;

const leasedAssetDefaultValues = {
  lessorName: '',
  lessorAddr: '',
  lessorCity: '',
  lessorState: '',
  lessorZip: '',
  agreementNo: '',
  descr: '',
  quantity: '',
  dateOf: '',
  costOf: '',
  term: '',
  annualRent: '',
};

/**
 * useLeasedAssetInfo is a custom hook that returns the leased asset info and functions for updating it.
 *
 * @returns {Object} leasedAssets - The array of leased assets.
 * @returns {Function} setLeasedAssets - A function for updating the array of leased assets.
 * @returns {Object} initialValues - The initial values for the leased asset form.
 * @returns {Function} setInitialValues - A function for updating the initial values for the leased asset form.
 * @returns {Object} updating - The leased asset that is currently being updated, or null if none.
 * @returns {Function} setUpdating - A function for updating the leased asset that is currently being updated.
 */
const useLeasedAssetInfo = () => {
  const [leasedAssets, setLeasedAssets] = useState([]);
  const [updating, setUpdating] = useState(null);
  const [initialValues, setInitialValues] = useState(leasedAssetDefaultValues);

  return { leasedAssets, setLeasedAssets, initialValues, setInitialValues, updating, setUpdating };
};

/**
 * useLeasedAssetModal is a custom hook that returns the functions and state for the leased asset modal.
 *
 * @param {Object} param0 - An object containing the setUpdating and setInitialValues functions.
 * @returns {Object} leasedAssetModalController - An object containing functions for controlling the leased asset modal.
 * @returns {Function} setUpdating - A function for updating the leased asset that is currently being updated.
 * @returns {Function} setInitialValues - A function for updating the initial values for the leased asset form.
 * @returns {Function} resetAssetModal - A function that resets the leased asset modal.
 */
const useLeasedAssetModal = ({ setUpdating, setInitialValues }) => {
  const leasedAssetModalController = useModal();

  const resetAssetModal = () => {
    setUpdating(null);
    setInitialValues(leasedAssetDefaultValues);
    leasedAssetModalController.closeModal();
  };

  return {
    leasedAssetModalController,
    setUpdating,
    setInitialValues,
    resetAssetModal,
  };
};

/**
 * LeasedAssetTableProvider is a component that provides the LeasedAssetTableContext to its children.
 * It contains the leasedAssetModal and leasedAssetInfo objects that are passed down to the children
 * through the context.
 *
 * @param {Object} children - The children that the LeasedAssetTableProvider will wrap.
 */

export function LeasedAssetTableProvider({ children }) {
  const { setUpdating, setInitialValues, setLeasedAssets, ...restOfLeasedAssetInfo } =
    useLeasedAssetInfo();

  const leasedAssetModal = useLeasedAssetModal({ setInitialValues, setUpdating });

  return (
    <LeasedAssetTableContext.Provider
      value={{
        leasedAssetModal,
        leasedAssetInfo: {
          setUpdating,
          setInitialValues,
          setLeasedAssets,
          ...restOfLeasedAssetInfo,
        },
      }}
    >
      {children}
    </LeasedAssetTableContext.Provider>
  );
}
